import { Group } from '@visx/group'
import { Arc } from '@visx/shape'
import type { ChartResult, ChartSpec } from '../../../types/chart.js'
import { CHART_GRID_COLOR, getSeriesStyle } from './chartTheme.js'
import { useNls } from '../../../nls/nlsProvider.js'
import './chart.css'

export interface GaugeChartProps {
  spec: ChartSpec
  data: ChartResult
  width?: number
  height?: number
  className?: string
  testId?: string
}

export function GaugeChart({
  spec,
  data,
  width = 300,
  height = 240,
  className = '',
  testId,
}: GaugeChartProps) {
  const { t } = useNls()
  const target = typeof data.target === 'number'
    ? data.target
    : (spec.target?.kind === 'number' ? spec.target.value : 100)

  const value = typeof data.total === 'number'
    ? data.total
    : (data.series[0]?.points.reduce((acc, p) => acc + p.y, 0) ?? 0)

  const pct = target > 0 ? Math.min(Math.max(value / target, 0), 1) : 0
  const pctInt = Math.round(pct * 100)

  const size = Math.min(width, height)
  const outerRadius = Math.max(10, size / 2 - 20)
  const innerRadius = Math.max(5, outerRadius - 24)

  const startAngle = -Math.PI * 0.75
  const endAngle = Math.PI * 0.75
  const valueAngle = startAngle + pct * (endAngle - startAngle)

  const seriesStyle = getSeriesStyle(0, spec.style?.seriesColor?.primary)

  return (
    <svg
      width={width}
      height={height}
      className={`chartSvg ${className}`}
      data-testid={testId}
      role="img"
      aria-label={`${spec.title || 'Gauge'}: ${value} / ${target} (${pctInt}%)`}
    >
      <Group left={width / 2} top={height / 2 + 10}>
        {/* Track */}
        <Arc
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={CHART_GRID_COLOR}
        />
        {/* Value arc */}
        {pct > 0 && (
          <Arc
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            startAngle={startAngle}
            endAngle={valueAngle}
            fill={seriesStyle.fill}
          />
        )}
        {/* Center text */}
        <text
          textAnchor="middle"
          dy="-0.2em"
          className="chartGaugeCenterText"
          data-testid="chart-gauge-value"
        >
          {value.toLocaleString()}
        </text>
        <text
          textAnchor="middle"
          dy="1.5em"
          className="chartGaugeSubText"
          data-testid="chart-gauge-target"
        >
          {t('NLSChartTarget')}: {target.toLocaleString()} ({pctInt}%)
        </text>
      </Group>
    </svg>
  )
}
